import { motion } from 'framer-motion';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useMutation, useQuery } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';
import { CheckCircleIcon, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useDeviceId } from '@/hooks/useDeviceId';
import api from '@/utils/ky';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { NameDialog } from '../coach/coach-categories';
import { CUSTOMER_MODULES } from '../../components/customer.tsx';

interface CategoryProps {
  category: any;
  isLocked: boolean;
  onClick: () => void;
}

const CategoryCard = ({ category, isLocked, onClick }: CategoryProps) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className={cn(
      'relative group bg-white p-6 rounded-xl shadow-sm transition-all duration-200',
      !isLocked && 'hover:shadow-md cursor-pointer hover:scale-[1.02]',
      isLocked && 'opacity-75'
    )}
    onClick={() => !isLocked && onClick()}
  >
    <div className="flex items-start justify-between">
      <div>
        <span className="text-4xl mb-4 block">{category.icon}</span>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {category.title}
        </h3>
        <p className="text-sm text-gray-500">
          {category.description}
        </p>
      </div>

      {isLocked ? (
        <div className="text-gray-400">
          <LockClosedIcon className="h-5 w-5" />
        </div>
      ) : (
        <div className="text-blue-600">
          <CheckCircleIcon className="h-5 w-5" />
        </div>
      )}
    </div>
  </motion.div>
);

export default function CustomerCategories() {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [searchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isNameDialogOpen, setNameDialogOpen] = useState(false);
  const deviceId = useDeviceId();

  // Query for existing user
  const { data: userData, isLoading: isLoadingUser } = useQuery({
    queryKey: ['user', deviceId],
    queryFn: async () => {
      try {
        const response = await api
          .get('api/customer/users', {
            searchParams: { deviceId },
          })
          .json<{ userId: string; name: string; company: string }>();

        if (!response || !response.userId) {
          return null;
        }

        localStorage.setItem('user_name', response.name);
        localStorage.setItem('user_company', response.company);

        return response;
      } catch (error) {
        throw error;
      }
    },
    enabled: !!deviceId,
    retry: false,
  });

  // Create user mutation
  const { mutate: createUser, isPending: isCreatingUser } = useMutation({
    mutationFn: (data: {
      name: string;
      deviceId: string;
      function: string;
      code: string;
    }) =>
      api
        .post('api/customer/users', {
          json: data,
        })
        .json<{
          userId: string;
          deviceId: string;
          companyName: string;
        }>(),
    onSuccess: (response, variables) => {
      localStorage.setItem('user_name', variables.name);
      localStorage.setItem('user_company', response.companyName);

      posthog.capture('user_created', {
        deviceId: variables.deviceId,
        code: variables.code,
      });

      navigateToChat(variables.name, response.companyName, selectedCategory);
    },
    onError: () => {
      toast.error('Failed to create user. Please try again.');
    },
  });

  const navigateToChat = (userName: string, companyName: string, categoryId: string) => {
    const pathToChat = '/customer/chat?code=' + searchParams.get('code');
    navigate(pathToChat, {
      state: {
        userName,
        category: categoryId,
        companyName,
      },
    });
  };

  const handleNameSubmit = (name: string) => {
    setNameDialogOpen(false);
    if (deviceId) {
      createUser({
        name,
        deviceId,
        function: 'customer',
        code: searchParams.get('code') || '',
      });
    }
  };

  const handleCategorySelect = (category: any) => {
    setSelectedCategory(category.id);
    if (userData?.name) {
      navigateToChat(userData.name, userData.company, category.id);
    } else {
      setNameDialogOpen(true);
    }
  };

  if (!deviceId || isLoadingUser || isCreatingUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-6 py-12 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl mb-4">
            Choose Your Support Area
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Select a training module to practice specific customer support scenarios
          </p>
        </div>

        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {CUSTOMER_MODULES.map((category) => (
            <CategoryCard
              key={category.id}
              category={category}
              isLocked={category.locked}
              onClick={() => handleCategorySelect(category)}
            />
          ))}
        </div>

        <NameDialog
          isOpen={isNameDialogOpen}
          onClose={() => setNameDialogOpen(false)}
          onSubmit={handleNameSubmit}
        />
      </div>
    </div>
  );
}